import React, { useMemo, useState, useEffect } from 'react';
import { FileDown } from 'lucide-react';
import { DatePickerButton, FilterPanel } from '@/shared/components/ui';
import { parseMockDateToIso, extractRowDateIso } from '@/shared/utils/dateUtils';
import { DEPARTMENT_OPTIONS, POSITION_OPTIONS } from '@/shared/data';
import { Dropdown } from '@/shared/components/ui/form/Dropdown';

// Export modal with dataset toggle (attendance vs recent logs) and date filtering.
export const ExportAttendanceModal = ({ onClose, data }) => {
  const [dataset, setDataset] = useState('attendance'); // 'attendance' | 'recent'
  const [format, setFormat] = useState('csv'); // 'csv' | 'pdf'
  const [dept, setDept] = useState('');
  const [pos, setPos] = useState('');
  const [startDateMock, setStartDateMock] = useState(null);
  const [endDateMock, setEndDateMock] = useState(null);

  const attendanceSet = data?.attendance || { columns: data?.columns, rows: data?.rows };
  const recentSet = data?.recentLogs || data?.recent || null;

  const activeSet = dataset === 'attendance' ? attendanceSet : recentSet;
  const rows = Array.isArray(activeSet?.rows) ? activeSet.rows : [];
  const hasColumns = Array.isArray(activeSet?.columns) && activeSet.columns.length > 0;
  const baseColumns = hasColumns ? activeSet.columns.filter(c => c.key !== 'actions') : [];

  // Use shared util for robust date extraction
  const getRowDateIso = extractRowDateIso;

  // Compute unique ISO dates present in rows (optional informational use)
  const availableDates = useMemo(() => {
    const s = new Set();
    rows.forEach(r => { const iso = getRowDateIso(r); if (iso) s.add(iso); });
    return Array.from(s).sort();
  }, [rows]);

  // Compute date range ISO and validate range <= 31 days
  const startIso = startDateMock ? parseMockDateToIso(startDateMock) : '';
  const endIso = endDateMock ? parseMockDateToIso(endDateMock) : '';
  const rangeValid = useMemo(() => {
    if (!startIso || !endIso) return true; // allow incomplete range
    const a = new Date(startIso);
    const b = new Date(endIso);
    if (isNaN(a) || isNaN(b)) return false;
    if (b < a) return false;
    const diffDays = Math.ceil((b - a) / (1000 * 60 * 60 * 24));
    return diffDays <= 31;
  }, [startIso, endIso]);

  // Compose filters: department, position, date range
  const rowsFiltered = useMemo(() => {
    let out = rows;
    if (dept) out = out.filter(r => (r.department || r.dept) === dept);
    if (pos) out = out.filter(r => r.position === pos);
    if (startIso || endIso) {
      out = out.filter(r => {
        const iso = getRowDateIso(r);
        if (!iso) return false;
        if (startIso && iso < startIso) return false;
        if (endIso && iso > endIso) return false;
        return true;
      });
    }
    return out;
  }, [rows, dept, pos, startIso, endIso]);

  // When switching dataset, ensure selected date is valid for new dataset; otherwise clear it.
  useEffect(() => {
    // Keep previously selected date when switching datasets; allow any date
  }, [dataset]);

  const noData = rows.length === 0;

  const fileBaseName = dataset === 'recent' ? 'recent_logs' : 'attendance';
  const fileSuffix = startIso && endIso ? `${startIso}_to_${endIso}` : startIso || endIso || 'all';
  const fileName = `${fileBaseName}_${fileSuffix}.${format}`;

  const handleExport = () => {
    if (!rangeValid) return;
    const rowsToExport = rowsFiltered;
    if (!rowsToExport.length) return;

    let keys = [];
    let labels = [];
    if (baseColumns.length) {
      keys = baseColumns.map(c => c.key);
      labels = baseColumns.map(c => c.label || c.key);
    } else if (rowsToExport.length) {
      keys = Object.keys(rowsToExport[0]).filter(k => !k.startsWith('__'));
      labels = [...keys];
    }

    if (format === 'csv') {
      const lines = [labels.join(',')];
      rowsToExport.forEach(r => {
        lines.push(keys.map(k => `"${String(r[k] ?? '').replace(/"/g,'""')}"`).join(','));
      });
      const csv = '\ufeff' + lines.join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      onClose?.();
      return;
    }

    // PDF: open a print-friendly window and let the user Save as PDF
    if (format === 'pdf') {
      const win = window.open('', '_blank');
      if (!win) return;
      const styles = `
        <style>
          body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; padding: 16px; }
          h1 { font-size: 16px; margin: 0 0 12px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
          thead { background: #f9fafb; }
          .meta { color: #6b7280; font-size: 12px; margin-bottom: 12px; }
        </style>
      `;
      const header = `<h1>${fileBaseName === 'recent_logs' ? 'Recent Logs' : 'Attendance'} Report</h1>`;
      const meta = `<div class="meta">Format: PDF • ${startIso || '—'} to ${endIso || '—'} • Dept: ${dept || 'All'} • Position: ${pos || 'All'}</div>`;
      const thead = `<thead><tr>${labels.map(l => `<th>${l}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${rowsToExport.map(r => `<tr>${keys.map(k => `<td>${String(r[k] ?? '')}</td>`).join('')}</tr>`).join('')}</tbody>`;
      win.document.write(`<html><head><title>${fileName}</title>${styles}</head><body>${header}${meta}<table>${thead}${tbody}</table></body></html>`);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); win.close(); onClose?.(); }, 250);
    }
  };

  return (
    <div className="w-full max-w-xl">
      {/* Header */}
      <div className="flex items-start gap-4 px-8 py-6 border-b border-secondary-100">
        <div className="h-11 w-11 rounded-lg bg-primary-600/10 text-primary-600 grid place-items-center ring-1 ring-primary-600/20 shrink-0">
          <FileDown className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-secondary-900 leading-snug">Export logs</h2>
          <p className="text-sm text-secondary-600 mt-1">Choose a dataset, format, and filters. Date range is limited to 1 month.</p>
        </div>
      </div>

      {/* Body */}
      <div className="px-8 py-6 space-y-6">
        {/* Dataset + Format */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <fieldset aria-label="Dataset selection" className="flex items-center gap-2 flex-wrap" role="radiogroup">
            <legend className="sr-only">Dataset</legend>
            {[
              { key: 'attendance', label: 'Attendance' },
              { key: 'recent', label: 'Recent Logs' }
            ].map(opt => {
              const active = dataset === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setDataset(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-sm border font-medium transition focus:outline-none focus:ring-2 focus:ring-primary-500 ${active ? 'bg-primary-600 text-white border-primary-600 shadow-sm' : 'bg-white text-secondary-700 border-secondary-200 hover:bg-secondary-50'}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </fieldset>
          <div className="flex items-center gap-3">
            <label className="text-sm text-secondary-600">Format</label>
            <FilterPanel
              variant="dropDown"
              site={format}
              onSiteChange={setFormat}
              siteOptions={[
                { key: 'csv', label: 'CSV' },
                { key: 'pdf', label: 'PDF' },
              ]}
              autoWidth={true}
            />
          </div>
        </div>

        {/* Filters: Department, Position, Date Range */}
        <div className="flex flex-col gap-6">
          {/* <div className="flex items-center gap-3 flex-wrap">
            <FilterPanel
              variant="dropDown"
              site={dept}
              onSiteChange={setDept}
              siteOptions={[{ key: '', label: 'All Department' }, ...DEPARTMENT_OPTIONS]}
              autoWidth={true}
            />
            <FilterPanel
              variant="dropDown"
              site={pos}
              onSiteChange={setPos}
              siteOptions={[{ key: '', label: 'All Position' }, ...POSITION_OPTIONS]}
              autoWidth={true}
            />
          </div> */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">Start date</label>
              <DatePickerButton selectedDate={startDateMock} onChange={setStartDateMock} allowClear={true} />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">End date</label>
              <DatePickerButton selectedDate={endDateMock} onChange={setEndDateMock} allowClear={true} />
            </div>
            {!rangeValid && (
              <div className="sm:col-span-2 text-sm text-error-600">Range must be within 31 days and end date not before start date.</div>
            )}
          </div>
        </div>

        {/* File name preview */}
        {!noData && (
          <div className="text-[11px] text-secondary-500 font-mono bg-secondary-50 border border-secondary-100 rounded px-2 py-1 inline-flex items-center gap-1">
            <span className="opacity-70">File:</span> {fileName}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-secondary-100" />
      <div className="flex justify-end gap-3 px-8 py-6 rounded-b-2xl">
        <button
          onClick={onClose}
          className="inline-flex items-center rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        <button
          onClick={handleExport}
          disabled={noData || !rangeValid || rowsFiltered.length === 0}
          className="inline-flex items-center rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <FileDown className="h-4 w-4 mr-1" /> Export
        </button>
      </div>
    </div>
  );
};

export default ExportAttendanceModal;



